import router from '../router';
import { player } from '@/services/player';
import {
  fetchAccessTokens,
  fetchRefreshedAccessTokens,
  fetchUserInfo,
  isTokenExpired,
} from '../services/auth';
import { apolloClient } from '@/graphql/client';
import { getFileUrl, getUserInfo } from '../graphql/queries';
import Cookies from 'js-cookie';
import { uploadSongFile } from '@/services/media-upload';
import { ActionTree } from 'vuex';
import { SplashScreen } from '@capacitor/splash-screen';
import { Song } from '@/types';
import { getDefaultArtworkImage } from '@/helper';

const actions: ActionTree<any, any> = {
  // this happens every time the page is loaded fresh
  async bootApp({ commit, dispatch }, code = null) {
    await commit('setBlockAppLoading', true);

    // ignore the start up proces on certain pages
    // TODO we should probably handle this in a cleaner more
    // automated way at some point
    if (router.currentRoute.value.meta.guest) {
      commit('setBlockAppLoading', false);
      return;
    }

    // if we have been redirected here with an auth code
    // lets use it to fetch our access and refresh tokens and
    // save them to local storage
    code = code || router.currentRoute.value.query.code;
    if (code) {
      await dispatch('loginUser', code);
      await dispatch('fetchUserInfo');
    }

    // after that lets move on to making sure our user's
    // session is still active
    let token = (await localStorage.getItem('crate.auth.access_token')) || '';
    const refreshToken =
      (await localStorage.getItem('crate.auth.refresh_token')) || '';
    const user = JSON.parse(
      (await localStorage.getItem('crate.auth.user')) || '{}',
    );

    // if the token is expired doesnt exist or we have no user
    // lets make them re login. otherwise set them up as logged in.
    if (!token || !user.email || (!refreshToken && isTokenExpired(token))) {
      await commit('setBlockAppLoading', false);
      await router.push('/login');
    } else {
      if (isTokenExpired(token)) {
        let res: any;
        try {
          res = await fetchRefreshedAccessTokens(refreshToken);
        } catch (err) {
          commit('setBlockAppLoading', false);
          await router.push('/login');
          return;
        }
        token = res.data.access_token;
        localStorage.setItem('crate.auth.access_token', token);
      }

      // set up some stuff for the user to be logged in for the frontend
      await Promise.all([
        commit('setUser', user),
        commit('setAuthAccessToken', token),
        commit('setAuthRefreshToken', refreshToken),
        commit('setLoggedIn', true),
      ]);

      if (router.currentRoute.value.path === '/login') {
        await router.push('/');
      }

      // once were logged in lets fetch our app user
      const res = await apolloClient.query({
        query: getUserInfo,
      });

      // unblock
      await Promise.all([
        commit('setAppUser', res.data.userInfo),
        commit('setBlockAppLoading', false),
        SplashScreen.hide(),
      ]);
    }
  },
  async handleLocalLogin({ commit }, { accessToken, refreshToken, user }) {
    await Promise.all([
      commit('setUser', user),
      commit('setAuthAccessToken', accessToken),
      commit('setAuthRefreshToken', refreshToken),
      commit('setLoggedIn', true),
    ]);

    localStorage.setItem('crate.auth.access_token', accessToken);
    localStorage.setItem('crate.auth.refresh_token', refreshToken);
    localStorage.setItem('crate.auth.user', JSON.stringify(user));
    commit('setUser', user);

    // once were logged in lets fetch our app user
    const res = await apolloClient.query({
      query: getUserInfo,
    });

    const userInfo = res.data.userInfo;
    const viewedOnboarding = userInfo?.totalSongs === 0;
    let landingPage = '/';
    commit('setAppUser', userInfo);
    landingPage = viewedOnboarding ? '/onboarding' : '/';
    await router.replace(landingPage);
  },
  async fetchCrateUserInfo({ commit }) {
    const res = await apolloClient.query({
      query: getUserInfo,
      fetchPolicy: 'network-only',
    });

    commit('setAppUser', res.data.userInfo);
  },
  async fetchUserInfo({ commit }) {
    const userInfo = await fetchUserInfo(
      localStorage.getItem('crate.auth.access_token') || '',
    );

    localStorage.setItem('crate.auth.user', JSON.stringify(userInfo.data));
    commit('setUser', userInfo.data);
  },
  async loginUser({ commit }, code) {
    const res: any = await fetchAccessTokens(code);
    localStorage.setItem('crate.auth.access_token', res.data.access_token);
    localStorage.setItem('crate.auth.refresh_token', res.data.refresh_token);
    Cookies.set('crate-user-token', res.data.access_token, { expires: 1 });
    commit('setAuthAccessToken', res.data.access_token);
    commit('setAuthRefreshToken', res.data.refresh_token);
    commit('setLoggedIn', true);
  },
  async logoutUser({ commit }) {
    await localStorage.removeItem('crate.auth.access_token');
    await localStorage.removeItem('crate.auth.user');
    await localStorage.removeItem('crate.auth.refresh_token');
    commit('setLoggedIn', false);
    commit('setUser', null);
    commit('setAuthAccessToken', null);
  },
  resetPlayerPlaylist({ commit }) {
    commit('resetPlayerPlaylist');
  },
  async setPlayingFile({ commit, dispatch, state }, file) {
    if (file === null) {
      player.stop();
      commit('setPlayingFile', null);
    }

    if (state.player.playingFile && state.player.playingFile.id === file.id) {
      dispatch('playPause');
    } else {
      commit('setPlayingFile', file);

      // grab the url we want to play
      const toPlayUrl = file.optimizedUrl || file.cdnUrl;

      // if our url is expired lets refresh it
      // by auto fetching one from the api
      if (player.isUrlExpired(toPlayUrl)) {
        const res = await apolloClient.query({
          query: getFileUrl,
          variables: {
            id: file.id,
          },
        });
        player.setSource(res.data.file.optimizedUrl || res.data.file.cdnUrl);
      } else {
        player.setSource(toPlayUrl);
      }
      dispatch('play');
    }
  },
  playSong({ commit, dispatch }, { song, file }) {
    commit('resetPlayerPlaylist');
    commit('setPlayingSong', song);
    dispatch('setPlayingFile', file);
    dispatch('setNativeMedia', { song });
  },
  setPlayingSong({ commit }, song) {
    commit('setPlayingSong', song);
  },
  play({ commit }) {
    player.play();
    commit('setPaused', false);
  },
  pause({ commit }) {
    player.pause();
    commit('setPaused', true);
  },
  seekPercent(_, percent) {
    player.seekPercent(percent);
  },
  setVolume(_, volume) {
    player.setVolume(volume);
  },
  seekVolume(_, offset) {
    player.seekVolume(offset);
  },
  triggerLibraryRefresh({ commit }) {
    commit('setLibraryRefresh', true);
  },
  confirmLibraryRefresh({ commit }) {
    commit('setLibraryRefresh', false);
  },
  playPause({ state, dispatch }) {
    if (state.player.paused) {
      dispatch('play');
    } else {
      dispatch('pause');
    }
  },
  startFileUpload(
    { commit },
    { selectedFile, filename, songId, active, fileId, title, notes },
  ) {
    commit('startFileUpload', { file: selectedFile, songId, title, notes });
    uploadSongFile(
      selectedFile,
      filename,
      songId,
      active,
      fileId,
      title,
      notes,
    );
  },
  setUploadingFileId({ commit }, fileId) {
    commit('setUploadingFileId', fileId);
  },

  finishFileUpload({ commit }) {
    commit('finishFileUpload');
  },

  setUploadingFilePercentage({ commit }, percentage) {
    commit('setUploadingFilePercentage', percentage);
  },
  updatePlayerPlayingSongInfo({ commit }, { title, artist }) {
    commit('setPlayingSongInfo', { title, artist });
  },
  playPlaylist({ commit, dispatch, state }, { playlist, startId }) {
    // if the playlist is already playing and the start ID is the same just pause
    if (
      (state.player.playingPlaylist && !startId) ||
      (state.player.playingPlaylist?.id === playlist.id &&
        state.player.playingSong?.id === startId)
    ) {
      dispatch('playPause');
      return;
    }

    // filter out only the songs that have a file to play.
    const songs = playlist.songs.filter(
      (s: any) => s.files?.filter((f: any) => !!f.active).length > 0,
    );

    // once we filtered out proper songs get our start position
    const startSongIndex = startId
      ? songs.findIndex((s: Song) => s.id === startId)
      : 0;

    // set our new index
    commit('setPlaylistIndex', startSongIndex);

    // if single song is playing and player is clicked then and played song is same restart song
    if (
      state.player.playingFile &&
      !state.player.isPlaylistPlaying &&
      state.player.playingFile.id ===
        songs[startSongIndex].files.find((f: any) => !!f.active).id
    ) {
      player.restart();
      commit('setPaused', false);
      return;
    }

    // set values so playlist is playing
    commit('playPlaylist', {
      playlist,
      songs,
    });

    // set the playing song
    commit('setPlayingSong', songs[startSongIndex]);

    // set the proper playing file
    dispatch(
      'setPlayingFile',
      songs[startSongIndex].files.find((f: any) => !!f.active),
    );

    // set native media
    dispatch('setNativeMedia', { song: songs[0] });
  },
  songEnded({ commit, dispatch }) {
    commit('setCurrentTime', 0);
    commit('setPercentComplete', 0);
    commit('setPaused', true);
    dispatch('playNextPlaylistSong');
  },
  async playPrevSong({ commit, dispatch, state }) {
    // if single play - restart
    if (!state.player.isPlaylistPlaying) {
      player.restart();
      commit('setPaused', false);
      return;
    }

    // if playlist - if first song in queue restart
    if (state.player.isPlaylistPlaying && state.player.playlistIndex === 0) {
      player.restart();
      commit('setPaused', false);
      return;
    }

    // if playlist - if middle of queue and time is less than 3s restart else move to prev song
    if (
      state.player.isPlaylistPlaying &&
      state.player.playlistIndex > 0 &&
      state.player.currentTime >= 3
    ) {
      player.restart();
      commit('setPaused', false);
      return;
    }

    const playlisHasPrevSong =
      state.player.isPlaylistPlaying && state.player.playlistIndex > 0;
    if (!playlisHasPrevSong) {
      return;
    }

    dispatch('playPrevPlaylistSong');
  },
  async playNextSong({ dispatch, state }) {
    // if single play - pause
    if (!state.player.isPlaylistPlaying) {
      dispatch('pause');
      return;
    }
    // if playlist - if middle of queue move to next song
    if (state.player.isPlaylistPlaying && state.player.playlistIndex > 0) {
      dispatch('playNextPlaylistSong');
      return;
    }

    const playlistHasNextSong =
      state.player.isPlaylistPlaying &&
      state.player.playlistIndex < state.player.playlistSongs.length - 1;

    if (!playlistHasNextSong) {
      return;
    }

    dispatch('playNextPlaylistSong');
  },
  async playNextPlaylistSong({ commit, dispatch, state }) {
    if (
      !state.player.isPlaylistPlaying ||
      state.player.playlistIndex === state.player.playlistSongs.length - 1
    ) {
      return;
    }

    await commit('incrementPlaylistIndex');
    const nextSong = state.player.playlistSongs[state.player.playlistIndex];
    commit('setPlayingSong', nextSong);
    dispatch(
      'setPlayingFile',
      nextSong.files.find((f: any) => !!f.active),
    );
    dispatch('setNativeMedia', { song: nextSong });
  },
  async playPrevPlaylistSong({ commit, dispatch, state }) {
    if (!state.player.isPlaylistPlaying || state.player.playlistIndex === 0) {
      return;
    }
    await commit('decrementPlaylistIndex');
    const nextSong = state.player.playlistSongs[state.player.playlistIndex];
    commit('setPlayingSong', nextSong);
    dispatch(
      'setPlayingFile',
      nextSong.files.find((f: any) => !!f.active),
    );
    dispatch('setNativeMedia', { song: nextSong });
  },
  setNativeMedia(_, { song }) {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: song.title,
        artist: song.artist || 'Unknown artist',
        album: song.artist || 'Unknown artist',
        artwork: [
          {
            src:
              song.albumArtworkSmUrl ||
              getDefaultArtworkImage(song.defaultArtwork, 'MD'),
            sizes: '96x96',
            type: 'image/png',
          },
        ],
      });
    }
  },
  setPlayerTimes({ commit }, times) {
    commit('setPlayerTimes', times);
  },
  setKeyboardOpen({ commit }, open) {
    commit('setKeyboardOpen', open);
  },
};

export default actions;
